import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { ImageDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { ShareButton } from "@/components/ShareButton";

const SITE_URL = "https://neox.lk";

// A forwardable square image card of "today's song" for WhatsApp / social.
export const SongShareCard = ({ title, artist, greeting }) => {
  const { t, fontClass } = useLang();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const saveImage = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: "#1C140E", useCORS: true });
      const link = document.createElement("a");
      link.download = "neox-song-of-the-day.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      toast.error("Could not save the image on this phone. Please try the WhatsApp share instead.");
    } finally {
      setBusy(false);
    }
  };

  const waText = `🪔 ${t("sotd_title")}: ${title} — ${artist}\n${t("share_card_listen")}`;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="sotd-share">
      <ShareButton testid="sotd-wa" text={waText} url={SITE_URL} dark />
      <button
        type="button"
        onClick={saveImage}
        disabled={busy}
        data-testid="sotd-save-image"
        className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm transition-transform active:scale-95 disabled:opacity-60"
        style={{ background: "rgba(255,255,255,0.12)", color: "#fff" }}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ImageDown size={16} />}
        {t("sotd_save_img")}
      </button>

      {/* Hidden square share card (1080x1080) */}
      <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden="true">
        <div
          ref={cardRef}
          style={{
            width: 1080, height: 1080,
            background: "linear-gradient(135deg, #1C140E 0%, #3B2A16 55%, #92400E 150%)",
            color: "#fff", display: "flex", flexDirection: "column", alignItems: "center",
            padding: "90px 80px", boxSizing: "border-box", textAlign: "center",
          }}
        >
          <div style={{ fontSize: 96, lineHeight: 1 }}>🪔</div>
          <div className="font-en" style={{ fontSize: 30, letterSpacing: 8, color: "#F5C97A", marginTop: 22, fontWeight: 800, textTransform: "uppercase" }}>
            {t("sotd_title")}
          </div>

          <div className={fontClass()} style={{ fontSize: 74, fontWeight: 800, color: "#fff", marginTop: 30, lineHeight: 1.25 }}>
            {title}
          </div>
          <div className="font-en" style={{ fontSize: 36, color: "rgba(255,255,255,0.72)", marginTop: 18 }}>
            {artist}
          </div>

          <div style={{ width: 130, height: 5, background: "#92400E", borderRadius: 5, margin: "40px 0" }} />

          <div className={fontClass()} style={{ fontSize: 38, fontWeight: 700, color: "#F5C97A", lineHeight: 1.5 }}>
            {greeting}
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: "flex", alignItems: "center", gap: 34 }}>
            <div style={{ background: "#fff", borderRadius: 22, padding: 22 }}>
              <QRCodeCanvas value={SITE_URL} size={170} bgColor="#ffffff" fgColor="#1C140E" level="M" includeMargin={false} />
            </div>
            <div style={{ textAlign: "left" }}>
              <div className={fontClass()} style={{ fontSize: 34, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>
                {t("share_card_listen")}
              </div>
              <div className="font-en" style={{ fontSize: 60, fontWeight: 800, color: "#fff", letterSpacing: 2, marginTop: 6 }}>
                neox.lk
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
